import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { HeaderTooltip } from '../HeaderTooltip';
import {
  singlePoolPayoutCellTooltip,
  traditionalLtcPresentation,
} from './traditionalLtcPresentation';
import type { StrategyPresentation } from './types';

// Short-Term Care Insurance — traditional/standalone LTC plus the product's
// DEFINING lifetime benefit-period (day) cap. Reuses the traditional
// single-pool presentation wholesale and layers the day cap into the two
// payout surfaces that omitted it (P2-25): the payout column formula gains
// the "remaining benefit-period days" term, and the per-row OOP itemization
// gains a "benefit period exhausted · Nd" line fed from the engine's day
// accounting. Once the period exhausts, rows pay $0 with money still in the
// dollar pool — without these the row-level math is unexplained.

// Engine day accounting (shortTermCareStrategy): payableDays =
// min(eligibleDays, periodDaysRemaining), where eligibleDays = 30 − <2-ADL
// days − WP days. `facts.coverageEligibleDays` carries the period-CLAMPED
// payableDays, so the days the benefit period stranded this row are the
// gate-eligible days minus the clamped count. `prePolicyDays` is subtracted
// so a pre-policy row (no gates consumed, 0 payable) doesn't read as
// period-stranded.
function benefitPeriodStrandedDays(month: MonthlyEntry): number {
  const wpDays =
    month.facts.waitingPeriodDaysConsumedHome + month.facts.waitingPeriodDaysConsumedFacility;
  const gateEligibleDays =
    DAYS_IN_MONTH -
    month.facts.non2AdlDays -
    wpDays -
    month.events.ineligibleDayBreakdown.prePolicyDays;
  return Math.max(gateEligibleDays - month.facts.coverageEligibleDays, 0);
}

export const shortTermCarePresentation: StrategyPresentation = {
  ...traditionalLtcPresentation,

  payoutCellTooltip: ({ month, terms }) =>
    singlePoolPayoutCellTooltip({
      month,
      terms,
      benefitPeriodStrandedDays: benefitPeriodStrandedDays(month),
    }),

  copy: {
    ...traditionalLtcPresentation.copy,
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month, with cumulative dollars paid by the policy through this month in parentheses. STCI caps lifetime PAID days at the benefit period — once those days are consumed the policy pays $0 even with dollars left in the pool."
        formulas={[
          {
            label: 'Payable days',
            expression: 'min(eligible days, remaining benefit-period days)',
          },
          {
            label: 'Reimbursement mode',
            expression: 'min(eligible cost, monthly maximum, pool remaining)',
          },
          { label: 'Indemnity mode', expression: 'min(monthly maximum, pool remaining)' },
        ]}
        notes="Eligible cost excludes waiting-period days and days the insured fails the 2-ADL trigger; eligible days are further capped by the remaining benefit-period days."
      />
    ),
  },
};
